import React, { useEffect, useState, useRef, memo, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, User, Sparkles, Play, Pause, RotateCcw, Database, Package, Users, CheckCheck, Clock, MapPin, Truck, Phone, MessageCircle, Monitor, Calendar, Mail, FileText, Loader2 } from 'lucide-react';
const STAGE_W = 1280;
const STAGE_H = 720;
// =================== Time markers ===================
const T = {
  QUESTION: 0.5,
  CAPTION_1: 1.5,
  FETCH_START: 4.0,
  SYSTEMS_IN: 5.0,
  LINES_ANIMATE: 6.0,
  FETCH_TAG: 6.5,
  PROCESSING: 7.5,
  ANSWER: 10.0,
  ANSWER_CHIPS: 11.0,
  CAPTION_2: 11.5,
  RESCHEDULE_Q: 14.0,
  ACTION_PANEL: 15.0,
  ACTION_STEP_1: 15.5,
  ACTION_STEP_2: 16.5,
  ACTION_STEP_3: 17.5,
  ORDER_UPDATE: 18.0,
  AI_CONFIRM: 19.0,
  CAPTION_3: 19.5,
  MULTI_CHANNEL: 22.0,
  CHANNEL_1: 22.5,
  CHANNEL_2: 23.0,
  CHANNEL_3: 23.5,
  SYNC_LINES: 24.0,
  CAPTION_4: 24.5,
  SHOWCASE: 27.0,
  SHOWCASE_1: 27.0,
  SHOWCASE_2: 28.2,
  SHOWCASE_3: 29.4,
  SHOWCASE_4: 30.6,
  IMPACT: 32.0,
  IMPACT_LINE_1: 32.5,
  IMPACT_LINE_2: 33.5,
  IMPACT_LINE_3: 34.5
};
export const SLIDE_6_DURATION = 36;
// =================== Captions ===================
function getCaption(t: number): {
  id: string;
  text: string;
} {
  if (t < T.FETCH_START) return {
    id: 'a',
    text: 'Not just understanding…'
  };
  if (t < T.ANSWER) return {
    id: 'b',
    text: 'Fetching live data'
  };
  if (t < T.RESCHEDULE_Q) return {
    id: 'c',
    text: 'Live answers.'
  };
  if (t < T.MULTI_CHANNEL) return {
    id: 'd',
    text: 'Real actions.'
  };
  if (t < T.SHOWCASE) return {
    id: 'e',
    text: 'Across every channel.'
  };
  return {
    id: 'f',
    text: ''
  };
}
// =================== Customer ===================
const CUSTOMER = {
  name: 'Ahmed Al-Mansoori',
  orderId: '#AE-48217'
};
// =================== PositionedNode (cards with z:5) ===================
const PositionedNode = memo(({
  x,
  y,
  children,
  className = ''
}: {
  x: number;
  y: number;
  children: React.ReactNode;
  className?: string;
}) => <motion.div className={`absolute ${className}`} style={{
  left: x,
  top: y,
  zIndex: 5
}} data-id="element-2158">
      {children}
    </motion.div>);
PositionedNode.displayName = 'PositionedNode';
// =================== Chat Bubble ===================
const ChatBubble = memo(({
  isUser,
  children,
  delay = 0
}: {
  isUser: boolean;
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 12,
  scale: 0.96
}} animate={{
  opacity: 1,
  y: 0,
  scale: 1
}} transition={{
  duration: 0.4,
  delay,
  ease: [0.16, 1, 0.3, 1]
}} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`} data-id="element-2159">
      <div className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${isUser ? 'bg-white/[0.08] text-white/90 border border-white/10' : 'bg-emerald-500/10 text-white/95 border border-emerald-400/20'}`} data-id="element-2160">
        {children}
      </div>
    </motion.div>);
ChatBubble.displayName = 'ChatBubble';
// =================== System Card ===================
const SystemCard = memo(({
  icon: Icon,
  label,
  delay = 0
}: {
  icon: React.ElementType;
  label: string;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  x: 20,
  scale: 0.9
}} animate={{
  opacity: 1,
  x: 0,
  scale: 1
}} transition={{
  duration: 0.5,
  delay,
  ease: [0.16, 1, 0.3, 1]
}} className="flex items-center gap-3 px-4 py-3 bg-white/[0.04] border border-white/10 backdrop-blur-xl rounded-xl" data-id="element-2161">
      <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center" data-id="element-2162">
        <Icon className="w-4 h-4 text-emerald-400" data-id="element-2163" />
      </div>
      <div className="flex-1" data-id="element-2164">
        <div className="text-xs font-semibold text-white/90" data-id="element-2165">{label}</div>
        <div className="flex items-center gap-1.5 mt-0.5" data-id="element-2166">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-2167" />
          <div className="text-[10px] text-white/50" data-id="element-2168">Connected</div>
        </div>
      </div>
    </motion.div>);
SystemCard.displayName = 'SystemCard';
// =================== Action Step ===================
const ActionStep = memo(({
  label,
  status,
  delay = 0
}: {
  label: string;
  status: 'pending' | 'loading' | 'done';
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  x: -8
}} animate={{
  opacity: 1,
  x: 0
}} transition={{
  duration: 0.3,
  delay
}} className="flex items-center gap-2.5 py-2" data-id="element-2169">
      {status === 'pending' && <div className="w-4 h-4 rounded-full border-2 border-white/20" data-id="element-2170" />}
      {status === 'loading' && <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" data-id="element-2171" />}
      {status === 'done' && <motion.div initial={{
    scale: 0
  }} animate={{
    scale: 1
  }} transition={{
    duration: 0.3,
    ease: [0.16, 1, 0.3, 1]
  }} className="w-4 h-4 rounded-full bg-emerald-500/20 flex items-center justify-center" data-id="element-2172">
          <CheckCheck className="w-3 h-3 text-emerald-400" data-id="element-2173" />
        </motion.div>}
      <div className={`text-xs ${status === 'done' ? 'text-white/90' : 'text-white/50'}`} data-id="element-2174">
        {label}
      </div>
    </motion.div>);
ActionStep.displayName = 'ActionStep';
// =================== Showcase Card ===================
const ShowcaseCard = memo(({
  icon: Icon,
  label,
  tag
}: {
  icon: React.ElementType;
  label: string;
  tag: string;
}) => <motion.div initial={{
  opacity: 0,
  scale: 0.9
}} animate={{
  opacity: 1,
  scale: 1
}} exit={{
  opacity: 0,
  scale: 0.9
}} transition={{
  duration: 0.4,
  ease: [0.16, 1, 0.3, 1]
}} className="w-80 bg-white/[0.04] border border-white/10 backdrop-blur-xl rounded-2xl p-6" data-id="element-2175">
      <div className="flex items-center gap-3 mb-4" data-id="element-2176">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center" data-id="element-2177">
          <Icon className="w-5 h-5 text-emerald-400" data-id="element-2178" />
        </div>
        <div className="text-sm font-semibold text-white/90" data-id="element-2179">{label}</div>
      </div>
      <div className="flex items-center gap-2" data-id="element-2180">
        <div className="w-2 h-2 rounded-full bg-emerald-400" data-id="element-2181" />
        <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-emerald-400/90" data-id="element-2182">
          {tag}
        </div>
      </div>
    </motion.div>);
ShowcaseCard.displayName = 'ShowcaseCard';
// =================== Main Component ===================
interface PresentationSlide6Props {
  externalElapsed?: number;
  hideChrome?: boolean;
}
export function PresentationSlide6({
  externalElapsed,
  hideChrome = false
}: PresentationSlide6Props) {
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(true);
  const startedAtRef = useRef<number>(Date.now());
  const pausedAtRef = useRef<number>(0);
  // If externalElapsed is provided, use it directly (deck mode)
  const t = externalElapsed !== undefined ? externalElapsed : elapsed;
  // Internal RAF timer (only when not controlled by deck)
  useEffect(() => {
    if (externalElapsed !== undefined) return;
    if (!playing) {
      pausedAtRef.current = elapsed;
      return;
    }
    startedAtRef.current = Date.now() - pausedAtRef.current * 1000;
    let raf: number;
    const tick = () => {
      const newT = (Date.now() - startedAtRef.current) / 1000;
      if (newT >= SLIDE_6_DURATION) {
        setElapsed(SLIDE_6_DURATION);
        setPlaying(false);
        return;
      }
      setElapsed(newT);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, externalElapsed]);
  const replay = () => {
    pausedAtRef.current = 0;
    setElapsed(0);
    setPlaying(true);
  };
  const caption = getCaption(t);
  // Action panel steps
  const actionSteps = [{
    label: 'Connecting to delivery system',
    status: t < T.ACTION_STEP_1 ? 'pending' : t < T.ACTION_STEP_2 ? 'loading' : 'done'
  }, {
    label: 'Updating schedule → tomorrow',
    status: t < T.ACTION_STEP_2 ? 'pending' : t < T.ACTION_STEP_3 ? 'loading' : 'done'
  }, {
    label: 'Confirming change',
    status: t < T.ACTION_STEP_3 ? 'pending' : t < T.ORDER_UPDATE ? 'loading' : 'done'
  }] as const;
  // Showcase items
  const showcaseItems = [{
    icon: Calendar,
    label: 'Appointment Booked',
    tag: 'Booking'
  }, {
    icon: Mail,
    label: 'Email Sent',
    tag: 'Triggering workflows'
  }, {
    icon: Users,
    label: 'CRM Record Updated',
    tag: 'Updating'
  }, {
    icon: FileText,
    label: 'Support Ticket Created',
    tag: 'Executing tasks'
  }];
  const currentShowcase = t >= T.SHOWCASE_1 && t < T.SHOWCASE_2 ? 0 : t >= T.SHOWCASE_2 && t < T.SHOWCASE_3 ? 1 : t >= T.SHOWCASE_3 && t < T.SHOWCASE_4 ? 2 : t >= T.SHOWCASE_4 && t < T.IMPACT ? 3 : -1;
  // Impact text reveal (word-by-word blur)
  const impactLines = [{
    text: 'Not just responding…',
    color: 'text-white/40',
    start: T.IMPACT_LINE_1
  }, {
    text: 'Taking action.',
    color: 'text-white',
    start: T.IMPACT_LINE_2
  }, {
    text: 'VEXU gets things done.',
    color: 'text-emerald-400',
    start: T.IMPACT_LINE_3
  }];
  return <div className="relative w-screen h-screen overflow-hidden bg-[#06080F]" data-id="element-2183">
      {/* Ambient blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-2184">
        <motion.div className="absolute w-[800px] h-[800px] rounded-full opacity-[0.03]" style={{
        background: 'radial-gradient(circle, #10D96A 0%, transparent 70%)',
        left: '10%',
        top: '20%'
      }} animate={{
        scale: [1, 1.1, 1],
        opacity: [0.03, 0.05, 0.03]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-2185" />
        <motion.div className="absolute w-[600px] h-[600px] rounded-full opacity-[0.02]" style={{
        background: 'radial-gradient(circle, #FBBF24 0%, transparent 70%)',
        right: '15%',
        bottom: '15%'
      }} animate={{
        scale: [1, 1.15, 1],
        opacity: [0.02, 0.04, 0.02]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-2186" />
      </div>

      {/* Scaled stage container */}
      <div className="absolute inset-0 flex items-center justify-center" style={{
      '--stage-scale': Math.min(window.innerWidth / STAGE_W, window.innerHeight / STAGE_H)
    } as React.CSSProperties} data-id="element-2187">
        <div className="relative origin-center" style={{
        width: STAGE_W,
        height: STAGE_H,
        transform: 'scale(var(--stage-scale))'
      }} data-id="element-2188">
          {/* SVG layer for connection lines (z:2) */}
          <svg className="absolute inset-0 pointer-events-none" style={{
          zIndex: 2
        }} viewBox={`0 0 ${STAGE_W} ${STAGE_H}`} data-id="element-2189">
            <defs data-id="element-2190">
              <filter id="glow6" data-id="element-2191">
                <feGaussianBlur stdDeviation="3" result="coloredBlur" data-id="element-2192" />
                <feMerge data-id="element-2193">
                  <feMergeNode in="coloredBlur" data-id="element-2194" />
                  <feMergeNode in="SourceGraphic" data-id="element-2195" />
                </feMerge>
              </filter>
            </defs>

            {/* Data flow lines (Chapter 2) */}
            {t >= T.LINES_ANIMATE && t < T.ANSWER && <>
                {/* CRM → VEXU */}
                <DataFlowLine x1={920} y1={200} x2={720} y2={280} delay={0} t={t - T.LINES_ANIMATE} data-id="element-2196" />
                {/* Order → VEXU */}
                <DataFlowLine x1={920} y1={280} x2={720} y2={280} delay={0.15} t={t - T.LINES_ANIMATE} data-id="element-2197" />
                {/* Database → VEXU */}
                <DataFlowLine x1={920} y1={360} x2={720} y2={280} delay={0.3} t={t - T.LINES_ANIMATE} data-id="element-2198" />
              </>}

            {/* Multi-channel sync lines (Chapter 5) */}
            {t >= T.SYNC_LINES && t < T.SHOWCASE && <>
                {/* VEXU → Call */}
                <SyncLine x1={640} y1={340} x2={340} y2={340} delay={0} t={t - T.SYNC_LINES} data-id="element-2199" />
                {/* VEXU → WhatsApp */}
                <SyncLine x1={640} y1={340} x2={640} y2={340} delay={0.1} t={t - T.SYNC_LINES} data-id="element-2200" />
                {/* VEXU → Dashboard */}
                <SyncLine x1={640} y1={340} x2={940} y2={340} delay={0.2} t={t - T.SYNC_LINES} data-id="element-2201" />
              </>}
          </svg>

          {/* CHAPTER 1 — QUESTION */}
          {t >= T.QUESTION && t < T.FETCH_START && <PositionedNode x={280} y={140} data-id="element-2202">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            ease: [0.16, 1, 0.3, 1]
          }} className="w-[720px] bg-white/[0.04] border border-white/10 backdrop-blur-xl rounded-2xl overflow-hidden" data-id="element-2203">
                {/* Chat header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10" data-id="element-2204">
                  <div className="flex items-center gap-3" data-id="element-2205">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center" data-id="element-2206">
                      <Bot className="w-4 h-4 text-emerald-400" data-id="element-2207" />
                    </div>
                    <div data-id="element-2208">
                      <div className="text-xs font-semibold text-white/90" data-id="element-2209">
                        VEXU AI
                      </div>
                      <div className="text-[10px] text-white/50" data-id="element-2210">
                        {CUSTOMER.name}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-2211">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-2212" />
                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-emerald-400/90" data-id="element-2213">
                      Live
                    </div>
                  </div>
                </div>

                {/* Chat body */}
                <div className="p-5 h-[360px] overflow-y-auto" data-id="element-2214">
                  {t >= T.QUESTION && <ChatBubble isUser delay={0.3} data-id="element-2215">
                      What's the status of my order?
                    </ChatBubble>}
                </div>
              </motion.div>
            </PositionedNode>}

          {/* CHAPTER 2 — LIVE DATA FETCH */}
          {t >= T.FETCH_START && t < T.ANSWER && <>
              {/* Chat card (left half) */}
              <PositionedNode x={40} y={140} data-id="element-2216">
                <motion.div initial={{
              x: 240,
              scale: 1
            }} animate={{
              x: 0,
              scale: 0.85
            }} transition={{
              duration: 0.7,
              ease: [0.16, 1, 0.3, 1]
            }} className="w-[720px] bg-white/[0.04] border border-white/10 backdrop-blur-xl rounded-2xl overflow-hidden" data-id="element-2217">
                  <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10" data-id="element-2218">
                    <div className="flex items-center gap-3" data-id="element-2219">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center" data-id="element-2220">
                        <Bot className="w-4 h-4 text-emerald-400" data-id="element-2221" />
                      </div>
                      <div data-id="element-2222">
                        <div className="text-xs font-semibold text-white/90" data-id="element-2223">
                          VEXU AI
                        </div>
                        <div className="text-[10px] text-white/50" data-id="element-2224">
                          {CUSTOMER.name}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-2225">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-2226" />
                      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-emerald-400/90" data-id="element-2227">
                        Live
                      </div>
                    </div>
                  </div>
                  <div className="p-5 h-[360px] overflow-y-auto" data-id="element-2228">
                    <ChatBubble isUser data-id="element-2229">
                      What's the status of my order?
                    </ChatBubble>
                  </div>
                </motion.div>
              </PositionedNode>

              {/* System cards (right) */}
              {t >= T.SYSTEMS_IN && <PositionedNode x={880} y={180} data-id="element-2230">
                  <div className="w-64 space-y-3" data-id="element-2231">
                    <SystemCard icon={Users} label="CRM" delay={0} data-id="element-2232" />
                    <SystemCard icon={Package} label="Order System" delay={0.15} data-id="element-2233" />
                    <SystemCard icon={Database} label="Database" delay={0.3} data-id="element-2234" />
                  </div>
                </PositionedNode>}

              {/* VEXU node (center) */}
              {t >= T.FETCH_TAG && <PositionedNode x={680} y={260} data-id="element-2235">
                  <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.4,
              ease: [0.16, 1, 0.3, 1]
            }} className="relative" data-id="element-2236">
                    <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center border-2 border-emerald-400/30" data-id="element-2237">
                      <Sparkles className="w-6 h-6 text-emerald-400" data-id="element-2238" />
                    </div>
                    {t >= T.PROCESSING && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} className="absolute -bottom-10 left-1/2 -translate-x-1/2 whitespace-nowrap" data-id="element-2239">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-400/20 rounded-full" data-id="element-2240">
                          <Loader2 className="w-3 h-3 text-emerald-400 animate-spin" data-id="element-2241" />
                          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-emerald-400/90" data-id="element-2242">
                            Processing
                          </div>
                        </div>
                      </motion.div>}
                  </motion.div>
                </PositionedNode>}
            </>}

          {/* CHAPTER 3 — REAL ANSWER */}
          {t >= T.ANSWER && t < T.RESCHEDULE_Q && <PositionedNode x={280} y={140} data-id="element-2243">
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5
          }} className="w-[720px] bg-white/[0.04] border border-white/10 backdrop-blur-xl rounded-2xl overflow-hidden" data-id="element-2244">
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10" data-id="element-2245">
                  <div className="flex items-center gap-3" data-id="element-2246">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center" data-id="element-2247">
                      <Bot className="w-4 h-4 text-emerald-400" data-id="element-2248" />
                    </div>
                    <div data-id="element-2249">
                      <div className="text-xs font-semibold text-white/90" data-id="element-2250">
                        VEXU AI
                      </div>
                      <div className="text-[10px] text-white/50" data-id="element-2251">
                        {CUSTOMER.name}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-2252">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-2253" />
                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-emerald-400/90" data-id="element-2254">
                      Live
                    </div>
                  </div>
                </div>
                <div className="p-5 h-[360px] overflow-y-auto" data-id="element-2255">
                  <ChatBubble isUser data-id="element-2256">What's the status of my order?</ChatBubble>
                  <ChatBubble isUser={false} delay={0.3} data-id="element-2257">
                    <div data-id="element-2258">
                      Your order {CUSTOMER.orderId} is out for delivery and will
                      arrive today at 4:30 PM.
                    </div>
                    {t >= T.ANSWER_CHIPS && <motion.div initial={{
                  opacity: 0,
                  y: 4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.4,
                  delay: 0.2
                }} className="flex items-center gap-2 mt-3" data-id="element-2259">
                        <Chip icon={Clock} text="4:30 PM" delay={0} data-id="element-2260" />
                        <Chip icon={Truck} text="Out for delivery" delay={0.1} data-id="element-2261" />
                        <Chip icon={MapPin} text="Dubai" delay={0.2} data-id="element-2262" />
                      </motion.div>}
                  </ChatBubble>
                </div>
              </motion.div>
            </PositionedNode>}

          {/* CHAPTER 4 — AGENTIC ACTION */}
          {t >= T.RESCHEDULE_Q && t < T.MULTI_CHANNEL && <>
              {/* Chat card */}
              <PositionedNode x={80} y={100} data-id="element-2263">
                <div className="w-[560px] bg-white/[0.04] border border-white/10 backdrop-blur-xl rounded-2xl overflow-hidden" data-id="element-2264">
                  <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10" data-id="element-2265">
                    <div className="flex items-center gap-3" data-id="element-2266">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center" data-id="element-2267">
                        <Bot className="w-4 h-4 text-emerald-400" data-id="element-2268" />
                      </div>
                      <div data-id="element-2269">
                        <div className="text-xs font-semibold text-white/90" data-id="element-2270">
                          VEXU AI
                        </div>
                        <div className="text-[10px] text-white/50" data-id="element-2271">
                          {CUSTOMER.name}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-5 max-h-[280px] overflow-y-auto" data-id="element-2272">
                    <ChatBubble isUser data-id="element-2273">
                      What's the status of my order?
                    </ChatBubble>
                    <ChatBubble isUser={false} data-id="element-2274">
                      Your order {CUSTOMER.orderId} is out for delivery and will
                      arrive today at 4:30 PM.
                    </ChatBubble>
                    {t >= T.RESCHEDULE_Q && <ChatBubble isUser delay={0.3} data-id="element-2275">
                        Can you reschedule it for tomorrow?
                      </ChatBubble>}
                    {t >= T.AI_CONFIRM && <ChatBubble isUser={false} delay={0.3} data-id="element-2276">
                        Done — your delivery is rescheduled to tomorrow at 4:30
                        PM.
                      </ChatBubble>}
                  </div>

                  {/* Action panel */}
                  {t >= T.ACTION_PANEL && t < T.AI_CONFIRM && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4
              }} className="px-5 py-4 border-t border-white/10 bg-white/[0.02]" data-id="element-2277">
                      <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-emerald-400/90 mb-3" data-id="element-2278">
                        Executing action
                      </div>
                      {actionSteps.map((step, i) => <ActionStep key={i} label={step.label} status={step.status} delay={i * 0.1} data-id="element-2279" />)}
                    </motion.div>}
                </div>
              </PositionedNode>

              {/* Order update card */}
              {t >= T.ORDER_UPDATE && <PositionedNode x={720} y={240} data-id="element-2280">
                  <motion.div initial={{
              opacity: 0,
              scale: 0.9,
              rotateY: 0
            }} animate={{
              opacity: 1,
              scale: 1,
              rotateY: 360
            }} transition={{
              duration: 0.6,
              ease: [0.16, 1, 0.3, 1]
            }} className="w-72 bg-white/[0.04] border border-white/10 backdrop-blur-xl rounded-xl p-5" data-id="element-2281">
                    <div className="flex items-start justify-between mb-4" data-id="element-2282">
                      <div data-id="element-2283">
                        <div className="text-xs font-semibold text-white/90" data-id="element-2284">
                          Order {CUSTOMER.orderId}
                        </div>
                        <div className="text-[10px] text-white/50 mt-0.5" data-id="element-2285">
                          Delivery Schedule
                        </div>
                      </div>
                      <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  delay: 0.3,
                  duration: 0.3
                }} className="flex items-center gap-1.5 px-2 py-1 bg-emerald-500/10 border border-emerald-400/20 rounded-full" data-id="element-2286">
                        <CheckCheck className="w-3 h-3 text-emerald-400" data-id="element-2287" />
                        <div className="text-[9px] font-bold tracking-[0.2em] uppercase text-emerald-400/90" data-id="element-2288">
                          Updated
                        </div>
                      </motion.div>
                    </div>
                    <div className="space-y-2" data-id="element-2289">
                      <div className="flex items-center justify-between py-2 px-3 bg-white/[0.02] rounded-lg line-through opacity-40" data-id="element-2290">
                        <div className="text-xs text-white/60" data-id="element-2291">Today</div>
                        <div className="text-xs text-white/60" data-id="element-2292">4:30 PM</div>
                      </div>
                      <motion.div initial={{
                  opacity: 0,
                  y: 4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.2
                }} className="flex items-center justify-between py-2 px-3 bg-emerald-500/10 border border-emerald-400/20 rounded-lg" data-id="element-2293">
                        <div className="text-xs font-semibold text-white/90" data-id="element-2294">
                          Tomorrow
                        </div>
                        <div className="text-xs font-semibold text-emerald-400" data-id="element-2295">
                          4:30 PM
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                </PositionedNode>}
            </>}

          {/* CHAPTER 5 — MULTI-CHANNEL CONSISTENCY */}
          {t >= T.MULTI_CHANNEL && t < T.SHOWCASE && <>
              {/* Call */}
              {t >= T.CHANNEL_1 && <PositionedNode x={180} y={260} data-id="element-2296">
                  <ChannelCard icon={Phone} label="Call" content="Rescheduled · tomorrow 4:30 PM" delay={0} data-id="element-2297" />
                </PositionedNode>}

              {/* WhatsApp */}
              {t >= T.CHANNEL_2 && <PositionedNode x={500} y={260} data-id="element-2298">
                  <ChannelCard icon={MessageCircle} label="WhatsApp" content="Delivery rescheduled to tomorrow" delay={0.15} data-id="element-2299" />
                </PositionedNode>}

              {/* Dashboard */}
              {t >= T.CHANNEL_3 && <PositionedNode x={820} y={260} data-id="element-2300">
                  <ChannelCard icon={Monitor} label="Dashboard" content={`${CUSTOMER.orderId} · Rescheduled`} delay={0.3} data-id="element-2301" />
                </PositionedNode>}

              {/* Central VEXU node */}
              {t >= T.SYNC_LINES && <PositionedNode x={616} y={316} data-id="element-2302">
                  <motion.div initial={{
              opacity: 0,
              scale: 0
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.4,
              ease: [0.16, 1, 0.3, 1]
            }} className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center border-2 border-emerald-400/30" data-id="element-2303">
                    <Sparkles className="w-6 h-6 text-emerald-400" data-id="element-2304" />
                  </motion.div>
                </PositionedNode>}
            </>}

          {/* CHAPTER 6 — AGENTIC SHOWCASE */}
          {t >= T.SHOWCASE && t < T.IMPACT && <div className="absolute inset-0 flex items-center justify-center" data-id="element-2305">
              <AnimatePresence mode="wait" data-id="element-2306">
                {currentShowcase >= 0 && <ShowcaseCard key={currentShowcase} icon={showcaseItems[currentShowcase].icon} label={showcaseItems[currentShowcase].label} tag={showcaseItems[currentShowcase].tag} data-id="element-2307" />}
              </AnimatePresence>
            </div>}

          {/* CHAPTER 7 — IMPACT */}
          {t >= T.IMPACT && <>
              {/* Radial dim */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 1
          }} className="absolute inset-0 bg-black/60" style={{
            background: 'radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.8) 100%)'
          }} data-id="element-2308" />

              {/* Impact text */}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-6" data-id="element-2309">
                {impactLines.map((line, i) => <ImpactLine key={i} text={line.text} color={line.color} start={line.start} t={t} size={i === 1 ? 'large' : 'normal'} data-id="element-2310" />)}
              </div>
            </>}

          {/* Caption (top-center) */}
          <AnimatePresence mode="wait" data-id="element-2311">
            {caption.text && <motion.div key={caption.id} initial={{
            opacity: 0,
            y: -12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 12
          }} transition={{
            duration: 0.5,
            ease: [0.16, 1, 0.3, 1]
          }} className="absolute top-12 left-1/2 -translate-x-1/2 text-center" data-id="element-2312">
                <div className="text-sm font-light tracking-tight text-white/70" data-id="element-2313">
                  {caption.text}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Chrome (play/pause/replay + progress) — only when NOT hideChrome */}
      {!hideChrome && <>
          <div className="absolute top-6 right-6 z-50 flex items-center gap-2" data-id="element-2314">
            <button onClick={() => setPlaying(p => !p)} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.06] hover:bg-white/[0.12] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/85 hover:text-white transition-all" data-id="element-2315">
              {playing ? <>
                  <Pause className="w-3.5 h-3.5" data-id="element-2316" /> Pause
                </> : <>
                  <Play className="w-3.5 h-3.5" data-id="element-2317" /> Play
                </>}
            </button>
            <button onClick={replay} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.06] hover:bg-white/[0.12] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/85 hover:text-white transition-all" data-id="element-2318">
              <RotateCcw className="w-3.5 h-3.5" data-id="element-2319" /> Replay
            </button>
          </div>

          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-6" data-id="element-2320">
            <div className="relative h-1.5 rounded-full bg-white/[0.07] overflow-hidden" data-id="element-2321">
              <motion.div className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-400 via-emerald-300 to-amber-300 rounded-full" style={{
            width: `${t / SLIDE_6_DURATION * 100}%`
          }} data-id="element-2322" />
            </div>
            <div className="flex items-center justify-between mt-2 text-[9px] font-bold tracking-[0.25em] text-white/45 uppercase" data-id="element-2323">
              <span className="font-mono" data-id="element-2324">
                {Math.floor(t / 60)}:
                {String(Math.floor(t % 60)).padStart(2, '0')}
              </span>
              <span data-id="element-2325">Agentic Action</span>
              <span className="font-mono" data-id="element-2326">
                {Math.floor(SLIDE_6_DURATION / 60)}:
                {String(Math.floor(SLIDE_6_DURATION % 60)).padStart(2, '0')}
              </span>
            </div>
          </div>
        </>}
    </div>;
}
// =================== Helper Components ===================
const Chip = memo(({
  icon: Icon,
  text,
  delay
}: {
  icon: React.ElementType;
  text: string;
  delay: number;
}) => <motion.div initial={{
  opacity: 0,
  scale: 0.9
}} animate={{
  opacity: 1,
  scale: 1
}} transition={{
  duration: 0.3,
  delay
}} className="flex items-center gap-1.5 px-2.5 py-1 bg-white/[0.06] border border-white/10 rounded-full" data-id="element-2327">
      <Icon className="w-3 h-3 text-amber-400" data-id="element-2328" />
      <div className="text-[10px] font-semibold text-white/80" data-id="element-2329">{text}</div>
    </motion.div>);
Chip.displayName = 'Chip';
const ChannelCard = memo(({
  icon: Icon,
  label,
  content,
  delay
}: {
  icon: React.ElementType;
  label: string;
  content: string;
  delay: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20,
  scale: 0.9
}} animate={{
  opacity: 1,
  y: 0,
  scale: 1
}} transition={{
  duration: 0.5,
  delay,
  ease: [0.16, 1, 0.3, 1]
}} className="w-64 bg-white/[0.04] border border-white/10 backdrop-blur-xl rounded-xl p-4" data-id="element-2330">
      <div className="flex items-center gap-2.5 mb-3" data-id="element-2331">
        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center" data-id="element-2332">
          <Icon className="w-4 h-4 text-emerald-400" data-id="element-2333" />
        </div>
        <div className="text-xs font-semibold text-white/90" data-id="element-2334">{label}</div>
      </div>
      <div className="text-xs text-white/70 leading-relaxed" data-id="element-2335">{content}</div>
      <div className="flex items-center gap-1.5 mt-3" data-id="element-2336">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" data-id="element-2337" />
        <div className="text-[9px] font-bold tracking-[0.2em] uppercase text-emerald-400/90" data-id="element-2338">
          Synced
        </div>
      </div>
    </motion.div>);
ChannelCard.displayName = 'ChannelCard';
const DataFlowLine = memo(({
  x1,
  y1,
  x2,
  y2,
  delay,
  t
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  delay: number;
  t: number;
}) => {
  const len = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
  const progress = Math.max(0, Math.min(1, (t - delay) / 0.8));
  const offset = len * (1 - progress);
  return <g data-id="element-2339">
        {/* Backing glow line */}
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#10D96A" strokeWidth="3" opacity="0.15" data-id="element-2340" />
        {/* Main animated line */}
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#10D96A" strokeWidth="2" strokeDasharray={len} strokeDashoffset={offset} opacity="0.8" data-id="element-2341" />
      </g>;
});
DataFlowLine.displayName = 'DataFlowLine';
const SyncLine = memo(({
  x1,
  y1,
  x2,
  y2,
  delay,
  t
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  delay: number;
  t: number;
}) => {
  const len = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
  const progress = Math.max(0, Math.min(1, (t - delay) / 0.6));
  const offset = len * (1 - progress);
  return <g data-id="element-2342">
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#10D96A" strokeWidth="2.5" opacity="0.12" data-id="element-2343" />
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#10D96A" strokeWidth="1.5" strokeDasharray={len} strokeDashoffset={offset} opacity="0.6" data-id="element-2344" />
      </g>;
});
SyncLine.displayName = 'SyncLine';
const ImpactLine = memo(({
  text,
  color,
  start,
  t,
  size = 'normal'
}: {
  text: string;
  color: string;
  start: number;
  t: number;
  size?: 'normal' | 'large';
}) => {
  const words = text.split(' ');
  const stagger = 0.08;
  return <div className={`flex items-center gap-3 ${size === 'large' ? 'text-5xl' : 'text-3xl'} font-light tracking-tight ${color}`} data-id="element-2345">
        {words.map((word, i) => {
      const wordStart = start + i * stagger;
      const progress = Math.max(0, Math.min(1, (t - wordStart) / 0.3));
      return <motion.span key={i} style={{
        filter: `blur(${(1 - progress) * 12}px)`,
        opacity: progress
      }} data-id="element-2346">
              {word}
            </motion.span>;
    })}
      </div>;
});
ImpactLine.displayName = 'ImpactLine';